import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Badge from '@mui/material/Badge';
import helpers from 'src/helpers';
import { PERMISSIONS_SYSTEM_CODE } from 'src/constants/permissions';

/**
 * useActivityTotal — CON-2026. The "new since you last looked" count for one
 * activity area (smartFormEntries, orders, partyGuests, groupMembers), or 0
 * without ACTIVITY_BADGES. The server sends only the areas the user may
 * see, so an area's own permissions need no second check here.
 */
export function useActivityTotal(area) {
  const userRolePermissions = useSelector((state) => state?.account?.user?.userRole?.permissions);
  const total = useSelector((state) => state?.activity?.areas?.[area]?.total);
  const hasActivityBadges = helpers.permissions.doesUserHavePermission(
    PERMISSIONS_SYSTEM_CODE.ACTIVITY_BADGES,
    userRolePermissions || [],
  );
  return hasActivityBadges && total > 0 ? total : 0;
}

/**
 * ActivityBadge — CON-2026. The sidebar badge for one activity area, styled
 * exactly like ChatBage.jsx (P1b's SmartFormsActivityBadge, made generic in
 * P1d).
 *
 * Reads only: the counts live in state.activity and arrive over the socket
 * (WebSocketContext asks for them on connect; the server pushes fresh ones
 * when something countable arrives or something is marked seen). Hidden
 * without ACTIVITY_BADGES and when the count is 0.
 */
function ActivityBadge({ area, children, className = '' }) {
  const badgeContent = useActivityTotal(area);

  return (
    <Badge
      className={className}
      badgeContent={badgeContent}
      color="secondary"
      sx={{
        '& .MuiBadge-badge':
        {
          fontSize: '10px',
          width: '15px',
          minWidth: '15px',
          height: '15px',
        },
      }}
    >
      {children}
    </Badge>
  );
}

ActivityBadge.propTypes = {
  area: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default ActivityBadge;
